import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import BusinessDataSources from 'components/BusinessDataSources'
import businessService from 'services/business'
import BusinessReviews from 'components/BusinessReviews'
import EmployeesSection from 'components/EmployeesSection'
import Icon from 'components/Icon'
import Button from 'components/Button'
import { FormattedMessage } from 'react-intl'
import cx from 'classnames'

import messages from './messages'

import {
  fetchBusiness,
  fetchBusinessReviews,
  selectCurrentBusiness,
  selectCurrentBusinessReviews,
  selectIsLoadingNextBatch,
  selectReviewsFilters,
  setIsLoadingNextBatch,
  setReviewsFilters,
} from 'store/business'

import { DUMMY_ZONE_NAME, selectCurrentZone, setIsInitialized } from 'store/global'

import BusinessSummary from 'components/BusinessSummary'
import BusinessMap from 'components/BusinessMap'

import ContactsSection from 'components/ContactsSection'
import LocationsSection from 'components/LocationsSection'
import OwnersSection from 'components/OwnersSection'
import LicenseTooltip from 'components/LicenseTooltip'
import usePrevious from '@rooks/use-previous'
import { SECTION_CONTACT, SECTION_DOCUMENTED, SECTION_EMPLOYEES, SECTION_LOCATIONS } from './specs'
import styles from './Business.scss'
import NavigationHeader from 'components/NavigationHeader/NavigationHeader'

const _ = require('lodash')

export default function BusinessPage() {
  const dispatch = useDispatch()

  const { businessId } = useParams()

  const currentBusiness = useSelector(selectCurrentBusiness)
  const businessReviews = useSelector(selectCurrentBusinessReviews)
  const isLoadingNextBatch = useSelector(selectIsLoadingNextBatch)

  const reviewsFilters = useSelector(selectReviewsFilters)

  const currentZone = useSelector(selectCurrentZone)
  const previousZone = usePrevious(currentZone)

  const { trackEvent } = useMatomo()

  const [displayedSection, setDisplayedSection] = useState(SECTION_DOCUMENTED)

  useEffect(() => {
    if (currentZone?.name === DUMMY_ZONE_NAME) return
    if (!previousZone && currentZone.name === DUMMY_ZONE_NAME) return

    dispatch(fetchBusiness({ businessId }))
    dispatch(setIsInitialized(true))
    dispatch(fetchBusinessReviews({ businessId }))
  }, [businessId, currentZone])

  if (_.isEqual(currentBusiness, {})) {
    return null
  }

  const onLoadMore = () => {
    if (isLoadingNextBatch) return
    dispatch(setIsLoadingNextBatch({ isLoading: true }))
    dispatch(
      fetchBusinessReviews({
        businessId: currentBusiness.id,
        from: businessReviews.length,
        filters: reviewsFilters,
      }),
    )
  }

  const onClickSection = newSection => {
    trackEvent({
      category: 'Business Page',
      action: `User clicked on section ${newSection} on business ${currentBusiness.businessName}`,
    })
    setDisplayedSection(newSection)
  }

  const onError = ev => {
    return true
  }

  const onReviewsFilter = selections => {
    if (isLoadingNextBatch) return
    const filters = selections.map(selection => selection.value)
    dispatch(setReviewsFilters({ filters }))
    dispatch(
      fetchBusinessReviews({
        businessId: currentBusiness.id,
        from: 0,
        filters,
      }),
    )
  }

  return (
    <div className={styles.container}>
      <nav className={styles.navigationHeaderWrapper}>
        <NavigationHeader entityParam={currentBusiness?.businessName} />
      </nav>

      <header className={styles.header}>
        <div className={styles.name}>{currentBusiness.businessName}</div>
        {currentBusiness.businessWebsite && (
          <section className={styles.link}>
            <a href={currentBusiness.businessWebsite} target='_blank' rel='noreferrer'>
              <Icon name='weblinks' size='regular' className={styles.linkIcon} />
              {/* {' '} */}
              {/* {currentBusiness.businessWebsite} */}
            </a>
          </section>
        )}
        {currentBusiness.logo && (
          <section className={styles.logoSection}>
            <img src={currentBusiness.logo} onError={onError} className={styles.logo} alt='' />
          </section>
        )}
        {currentBusiness.businessStructure && <div className={styles.form}>{currentBusiness.businessStructure}</div>}
        <div className={styles.dataSources}>
          <span className={styles.icons}>
            <BusinessDataSources dataSources={businessService.dataSources(currentBusiness)} />
          </span>
        </div>
      </header>
      <div className={styles.data}>
        <div className={styles.details}>
          <section className={styles.summary}>
            {currentBusiness.licenses && (
              <div className={styles.license}>
                <LicenseTooltip licenses={[currentBusiness.licenses]} />
              </div>
            )}
            <BusinessSummary isSpaced={false} />
          </section>
          <section className={styles.owners}>
            <OwnersSection owners={currentBusiness.businessOwner || []} />
          </section>
        </div>
        <div className={styles.map}>
          <BusinessMap business={currentBusiness} isExpanded />
        </div>
      </div>
      <div className={styles.business}>
        <div className={styles.businessContainer}>
          <section className={styles.tabs}>
            <div className={styles.tabContainer}>
              <section className={styles.toggleSections}>
                <Button
                  onClick={() => onClickSection(SECTION_DOCUMENTED)}
                  className={cx(styles.sectionHeader, {
                    [styles.active]: displayedSection === SECTION_DOCUMENTED,
                  })}
                >
                  <FormattedMessage {...messages[SECTION_DOCUMENTED]} />
                </Button>
                <Icon name='diamond' size='small' />
                <Button
                  onClick={() => onClickSection(SECTION_EMPLOYEES)}
                  className={cx(styles.sectionHeader, {
                    [styles.active]: displayedSection === SECTION_EMPLOYEES,
                  })}
                >
                  <FormattedMessage {...messages[SECTION_EMPLOYEES]} />
                </Button>
                <Icon name='diamond' size='small' />
                <Button
                  onClick={() => onClickSection(SECTION_LOCATIONS)}
                  className={cx(styles.sectionHeader, {
                    [styles.active]: displayedSection === SECTION_LOCATIONS,
                  })}
                >
                  <FormattedMessage {...messages[SECTION_LOCATIONS]} />
                </Button>
                <Icon name='diamond' size='small' />
                <Button
                  onClick={() => onClickSection(SECTION_CONTACT)}
                  className={cx(styles.sectionHeader, {
                    [styles.active]: displayedSection === SECTION_CONTACT,
                  })}
                >
                  <FormattedMessage {...messages[SECTION_CONTACT]} />
                </Button>
              </section>
            </div>
          </section>
          <span className={styles.data}>
            {displayedSection === SECTION_DOCUMENTED && (
              <BusinessReviews
                reviews={businessReviews}
                onScroll={onLoadMore}
                onFilter={onReviewsFilter}
                numberOfDocumentedItems={currentBusiness.numRevenueReviews}
                numberOfTotalItems={currentBusiness.reviewsCount}
                sources={currentBusiness.reviewSources}
              />
            )}
            {displayedSection === SECTION_EMPLOYEES && (
              <EmployeesSection
                employees={currentBusiness.employeeIdentities}
                enrichedEmployeeLocations={currentBusiness.enrichedEmployeeLocations}
              />
            )}
            {displayedSection === SECTION_LOCATIONS && (
              <LocationsSection
                locations={currentBusiness.businessAddresses}
                enrichedBusinessLocations={currentBusiness.enrichedBusinessLocations}
              />
            )}
            {displayedSection === SECTION_CONTACT && (
              <ContactsSection
                websiteLinks={currentBusiness.websiteLinks || []}
                phones={currentBusiness.phones || []}
                emails={currentBusiness.businessEmails || []}
                visibleSections={['weblinks', 'phones', 'emails']}
              />
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
